import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import ProfileTab from "./profile-tabs/ProfileTab";
import TestsTab from "./profile-tabs/TestsTab";
import RoutinesTab from "./profile-tabs/RoutinesTab";
import NutritionTab from "./profile-tabs/NutritionTab";
import DocumentsTab from "./profile-tabs/DocumentsTab";
import WellnessTab from "./profile-tabs/WellnessTab";
import AdminNotificationsTab from "./profile-tabs/AdminNotificationsTab";
import PaymentsSummaryTab from "./profile-tabs/PaymentsSummaryTab";
import TrainingsSummaryTab from "./profile-tabs/TrainingsSummaryTab";

const BASE_TABS = [
  { key: "profile", label: "Perfil" },
  { key: "tests", label: "Testeos Físicos" },
  { key: "routines", label: "Rutinas" },
  { key: "nutrition", label: "Nutrición" },
  { key: "wellness", label: "Wellness" },
  { key: "documents", label: "Documentos" },
  { key: "trainings-summary", label: "Resumen Entrenamientos" },
  { key: "payments-summary", label: "Resumen Pagos" },
];

const ADMIN_OWN_TABS = [
  { key: "profile", label: "Perfil" },
  { key: "notifications", label: "Notificaciones" },
];

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: current } = useAuth();
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = current?.role === "admin";
  const isProfesor = current?.role === "profesor";
  const isOwnAdminProfile = isAdmin && current?.user_id === userId;
  // Profesor: mismo set de tabs que admin salvo "Resumen Pagos"
  const TABS = isOwnAdminProfile
    ? ADMIN_OWN_TABS
    : (isProfesor ? BASE_TABS.filter((t) => t.key !== "payments-summary") : BASE_TABS);
  const TAB_KEYS = TABS.map((t) => t.key);
  const initialTab = TAB_KEYS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "profile";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync tab when URL ?tab= changes (deep-link from notification click)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && TAB_KEYS.includes(t) && t !== activeTab) setActiveTab(t);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams(key === "profile" ? {} : { tab: key }, { replace: true });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/users/${userId}`);
        setTarget(data);
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, navigate]);

  if (loading) return <Layout><div className="text-muted">Cargando...</div></Layout>;
  if (!target) return null;

  return (
    <Layout>
      <div className="mb-6">
        {isAdmin && (
          <button onClick={() => navigate("/users")} className="btn-ghost flex items-center gap-2 mb-4" data-testid="back-to-users">
            <ArrowLeft size={16} weight="bold" /> Volver a usuarios
          </button>
        )}
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center font-display font-black text-4xl overflow-hidden" data-testid="profile-avatar">
            {target.profile?.avatar_url ? (
              <img src={target.profile.avatar_url} alt={target.name} className="w-full h-full object-cover" />
            ) : (
              target.name?.[0]?.toUpperCase()
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted mb-1">Perfil de atleta</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase" data-testid="profile-name-heading">{target.name}</h1>
            <div className="text-muted text-sm mt-1">{target.email}</div>
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-app mb-8 overflow-x-auto" data-testid="profile-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            data-active={activeTab === t.key}
            className="tab-trigger whitespace-nowrap"
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === "profile" && <ProfileTab target={target} setTarget={setTarget} isAdmin={isAdmin} isOwner={current?.user_id === target.user_id} />}
        {activeTab === "tests" && <TestsTab userId={target.user_id} isAdmin={isAdmin} targetUser={target} />}
        {activeTab === "routines" && <RoutinesTab userId={target.user_id} isAdmin={isAdmin} targetUser={target} />}
        {activeTab === "nutrition" && <NutritionTab userId={target.user_id} isAdmin={isAdmin} isOwner={current?.user_id === target.user_id} targetUser={target} />}
        {activeTab === "wellness" && <WellnessTab userId={target.user_id} isAdmin={isAdmin} isOwner={current?.user_id === target.user_id} />}
        {activeTab === "documents" && <DocumentsTab userId={target.user_id} isAdmin={isAdmin} />}
        {activeTab === "trainings-summary" && <TrainingsSummaryTab userId={target.user_id} isAdmin={isAdmin} targetUser={target} />}
        {activeTab === "payments-summary" && <PaymentsSummaryTab userId={target.user_id} isAdmin={isAdmin} targetUser={target} />}
        {activeTab === "notifications" && isOwnAdminProfile && <AdminNotificationsTab />}
      </div>
    </Layout>
  );
}
